import { Link } from "react-router-dom";
import { Factory, ArrowRight, ArrowLeft, Ruler, Wrench, ShieldCheck } from "lucide-react";
import { Button } from "./ui/button";
import { useLang } from "../context/LanguageContext";
import { useSiteImages } from "../hooks/use-site-images";

const FABRICATION_ITEMS = [
  { key: "work_tables" },
  { key: "sinks" },
  { key: "shelves" },
  { key: "hoods" },
  { key: "trolleys" },
  { key: "counters" },
  { key: "cabinets" },
  { key: "custom" },
];

export function StainlessFactory() {
  const { t, isRTL } = useLang();
  const Arrow = isRTL ? ArrowLeft : ArrowRight;
  const f = t.stainlessFactory;
  const siteImage = useSiteImages();

  return (
    <section className="relative py-24 overflow-hidden bg-gradient-to-br from-slate-50 via-white to-cyan-50/30" data-testid="stainless-factory">
      {/* subtle steel texture */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(135deg, #0891b2 25%, transparent 25%), linear-gradient(225deg, #0891b2 25%, transparent 25%)",
          backgroundSize: "40px 40px",
        }}
      />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          {/* Left */}
          <div>
            <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.35em] text-[#ef4444] font-semibold mb-4 uppercase">
              <Factory className="w-3.5 h-3.5" />
              {f.eyebrow}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] mb-5 text-[#0891b2]">
              {f.title}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-8 text-lg">{f.subtitle}</p>

            <div className="grid sm:grid-cols-3 gap-4 mb-8">
              {[
                { icon: Ruler, label: f.badge1 },
                { icon: Wrench, label: f.badge2 },
                { icon: ShieldCheck, label: f.badge3 },
              ].map((b, i) => (
                <div key={i} data-testid={`sf-badge-${i}`} className="text-center bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                  <b.icon className="w-5 h-5 text-[#ef4444] mx-auto mb-2" />
                  <div className="text-xs font-semibold text-slate-700">{b.label}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-y-2.5 gap-x-6 mb-8">
              {FABRICATION_ITEMS.map((it, i) => (
                <div key={it.key} data-testid={`sf-item-${i}`} className="flex items-center gap-2.5 text-sm text-slate-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#ef4444] shrink-0" />
                  {f.items[it.key]}
                </div>
              ))}
            </div>

            <Link to="/fabrication">
              <Button data-testid="sf-cta" className="bg-[#ef4444] hover:bg-[#dc2626] text-white h-12 px-6 font-semibold shadow-md">
                {f.cta} <Arrow className="w-4 h-4 ms-2" />
              </Button>
            </Link>
          </div>

          {/* Right — illustrative visual */}
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden border border-slate-200 shadow-xl aspect-[4/5]">
              <img
                src={siteImage("home_factory", "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=1000&q=80")}
                alt="Stainless factory"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a]/85 via-[#0f172a]/20 to-transparent" />
              <div className="absolute bottom-6 start-6 end-6 text-white">
                <div className="inline-block bg-white/15 backdrop-blur border border-white/25 rounded-lg px-3 py-1.5 text-[10px] tracking-widest uppercase font-semibold text-red-200 mb-3">
                  {f.badgeInPhoto}
                </div>
                <div className="font-display text-xl leading-tight">{f.captionInPhoto}</div>
              </div>
            </div>

            {/* Floating stat card */}
            <div className="absolute -top-5 -end-4 lg:-end-8 bg-white text-slate-900 rounded-xl px-5 py-4 shadow-xl border border-slate-100">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">{f.statLabel}</div>
              <div className="font-display text-2xl text-[#0891b2]">{f.statValue}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
